import { fetchCurrentUser } from "@/features/auth/authService";
import { shouldRestoreSession } from "@/features/auth/authStorage";
import { authFailed, setCredentials } from "@/features/auth/authSlice";
import { usesLocalJwtAuth } from "@/services/api/baseUrl";

/**
 * Identity is a precondition of the app existing, not component state.
 *
 * The session used to be restored from a `useEffect` inside `AuthProvider`,
 * which made the number of `/me` calls a function of how often that component
 * mounted — twice per navigation, once StrictMode and an error boundary keyed
 * on `location.key` were both in play. Resolving it at module scope makes the
 * call count a property of the page load instead of the render tree: nothing in
 * React can mount its way into a second request.
 *
 * This is a single-flight, not a cache. Concurrent callers share one request; a
 * later caller gets a fresh one. Response caching belongs in HTTP headers on the
 * auth host rather than a hand-rolled store here — `maxAgeMs` is the one
 * exception, and it exists only so a burst of focus events cannot become a burst
 * of requests. Same shape as the `refreshPromise` single-flight in `baseApi.js`.
 */

let inFlight = null;
let lastResult = null;
let lastResolvedAt = 0;

/**
 * @typedef {object} SessionResult
 * @property {boolean} attempted   whether a request was actually made
 * @property {object|null} user
 * @property {boolean} unauthorized the server said the session is gone (401)
 */

/** @returns {Promise<SessionResult>} — never rejects. */
export function loadSession({ maxAgeMs = 0 } = {}) {
  if (inFlight) return inFlight;

  if (lastResult && maxAgeMs > 0 && Date.now() - lastResolvedAt < maxAgeMs) {
    return Promise.resolve(lastResult);
  }

  inFlight = fetchCurrentUser()
    .then(
      (user) => ({ attempted: true, user, unauthorized: false }),
      (error) => ({
        attempted: true,
        user: null,
        unauthorized: error?.response?.status === 401,
      }),
    )
    .then((result) => {
      lastResult = result;
      lastResolvedAt = Date.now();
      inFlight = null;
      return result;
    });

  return inFlight;
}

/** Boot path: JWT on localhost always validates; otherwise restore from the storage hint. */
export function restoreSessionOnBoot() {
  if (usesLocalJwtAuth()) {
    return loadSession();
  }
  if (!shouldRestoreSession()) {
    return Promise.resolve({ attempted: false, user: null, unauthorized: false });
  }
  return loadSession();
}

/** Drops the memo so the next load is a real request (logout, cross-tab login). */
export function resetSession() {
  inFlight = null;
  lastResult = null;
  lastResolvedAt = 0;
}

/**
 * Applies a result to the store.
 *
 * A failure only ends the session when the server actually said so, or when
 * there was no session to keep in the first place. A network blip during a
 * background revalidation leaves the user exactly where they are — being bounced
 * to the login screen because the wifi dropped for a second is the kind of thing
 * this file exists to prevent.
 */
export function applySession(store, result) {
  if (!result.attempted) return;

  if (result.user) {
    store.dispatch(setCredentials({ user: result.user }));
    return;
  }

  const hasSession = Boolean(store.getState().auth.user);
  if (result.unauthorized || !hasSession) {
    store.dispatch(authFailed());
  }
}
